import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '../services/api'

// Query Keys
export const lotteryKeys = {
  all: ['lottery'] as const,
  results: () => [...lotteryKeys.all, 'results'] as const,
  result: (round: number) => [...lotteryKeys.all, 'result', round] as const,
  latest: () => [...lotteryKeys.all, 'latest'] as const,
  statistics: () => [...lotteryKeys.all, 'statistics'] as const,
}

export const announcementKeys = {
  all: ['announcements'] as const,
  active: () => [...announcementKeys.all, 'active'] as const,
}

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  tiers: () => [...subscriptionKeys.all, 'tiers'] as const,
  public: () => [...subscriptionKeys.all, 'public'] as const,
}

// Lottery Hooks
export const useLatestLotteryResult = () => {
  return useQuery({
    queryKey: lotteryKeys.latest(),
    queryFn: () => apiService.getLatestLotteryResult(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
  })
}

export const useLotteryResults = (skip = 0, limit = 10) => {
  return useQuery({
    queryKey: [...lotteryKeys.results(), skip, limit],
    queryFn: () => apiService.getLotteryResults(skip, limit),
    staleTime: 1000 * 60 * 10, // 10 minutes
    placeholderData: (previousData: any) => previousData, // TanStack Query v5 replacement for keepPreviousData
  })
}

export const useLotteryResultByRound = (round: number) => {
  return useQuery({
    queryKey: lotteryKeys.result(round),
    queryFn: () => apiService.getLotteryResultByRound(round),
    enabled: !!round,
    staleTime: 1000 * 60 * 60, // 1 hour - historical data doesn't change
  })
}

export const useLotteryStatistics = () => {
  return useQuery({
    queryKey: lotteryKeys.statistics(),
    queryFn: () => apiService.getLotteryStatistics(),
    staleTime: 1000 * 60 * 15, // 15 minutes
  })
}

export const useCustomerWinningStats = () => {
  return useQuery({
    queryKey: [...lotteryKeys.all, 'customer-winning-stats'],
    queryFn: () => apiService.getCustomerWinningStats(),
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
  })
}

// Announcement Hooks
export const useActiveAnnouncements = () => {
  return useQuery({
    queryKey: announcementKeys.active(),
    queryFn: () => apiService.getActiveAnnouncements(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Subscription Hooks
export const usePublicSubscriptionTiers = () => {
  return useQuery({
    queryKey: subscriptionKeys.public(),
    queryFn: () => apiService.getPublicSubscriptionTiers(),
    staleTime: 1000 * 60 * 30, // 30 minutes
  })
}

// User Dashboard Hooks (protected)
export const useUserGeneratedNumbers = (skip = 0, limit = 10) => {
  return useQuery({
    queryKey: ['user', 'generated-numbers', skip, limit],
    queryFn: () => apiService.getCurrentUserGeneratedNumbers(skip, limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useUserSubscription = () => {
  return useQuery({
    queryKey: ['user', 'subscription'],
    queryFn: () => apiService.getCurrentUserSubscription(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

export const useUserWinningStats = () => {
  return useQuery({
    queryKey: ['user', 'winning-stats'],
    queryFn: () => apiService.getCurrentUserWinningStats(),
    staleTime: 1000 * 60 * 5,
  })
}

// Mutations
export const useGenerateNumbers = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: { user_id: string; round_target?: number; count?: number }) => 
      apiService.generateLotteryNumbers(data),
    onSuccess: () => {
      // Invalidate and refetch user generated numbers
      queryClient.invalidateQueries({ queryKey: ['user', 'generated-numbers'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'subscription'] })
    },
  })
}